import { type Node, type Edge } from "@xyflow/react";
import { type JsonPropertyNodeData, type MappedValueToken } from "./components/JsonPropertyNode";
import { ElementType, IElement, IElementDictionary } from "processEditor/model/IProcessData";
import { BpmnJsCliWrapper } from "processEditor/model/BpmnJsCliWrapper";
import { extractSourcePaths, extractEnvPaths, removeSourcePath, hasBareReference, hasHandlebarsReference, directTextMappingToCsharp, collectSourceRefs, formatSourceToken, resolveEditorMode, isPlainReferenceExpression, type VdmExpressionMode } from "./vdmMappingExpressions";

/** Parameter on a Foreach holding the schema of one item, which a VDM inside the loop maps from. */
export const FOREACH_ITEM_SCHEMA_KEY = "itemSchema";

const ROW_HEIGHT = 30;
const CONTENT_PADDING = 16;
const PANEL_GAP = 58;

export interface VdmMappingEntry {
    mode: "text" | "csharp";
    value: string;
    /** Source property type when this mapping was last saved. Used for schema-change detection. */
    sourceType?: string;
    /** Target property type when this mapping was last saved. Used for schema-change detection. */
    targetType?: string;
}

export interface JsonSchemaProperty {
    key: string;
    type: string;
    pathSegment?: string;
    displayType?: string;
    collapsedDisplayType?: string;
    children?: JsonSchemaProperty[];
    isCollapsed?: boolean;
    /**
     * Array nesting depth, written by the schema parser for every array node: 1 for a plain array,
     * 2 for an array of arrays, and so on. Nested arrays are flattened onto a single node, so this
     * is the only place the collapsed dimensions are recorded as data - `displayType` is
     * presentation only and must never be decoded for structure.
     */
    arrayDepth?: number;
}

/**
 * Array nesting depth of an array node. The parser writes `arrayDepth` on every array node, so this
 * only papers over hand-built trees; a depth below 1 is not a depth (and would throw in
 * `"[]".repeat()`), so unusable values also fall back to 1, same as the backend's ReadArrayDepth.
 */
export function resolveArrayDepth(prop: JsonSchemaProperty): number {
    return prop.arrayDepth !== undefined && prop.arrayDepth >= 1 ? prop.arrayDepth : 1;
}

export function collectSchemaPaths(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        paths.add(path);
        if (prop.children) {
            for (const childPath of collectSchemaPaths(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

/** Collects schema paths with their types (e.g. "foo.bar:string") so type changes are detectable */
export function collectSchemaPathsWithTypes(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        paths.add(`${path}:${prop.type}`);
        if (prop.children) {
            for (const childPath of collectSchemaPathsWithTypes(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

/** Type of the value a source or target path addresses, with trailing indexers resolved to the array's element type. */
export function findSchemaType(schema: JsonSchemaProperty[], path: string): string | undefined {
    return findSchemaProperty(schema, path)?.type;
}

export function collectLeafSchemaPaths(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        if (prop.type !== "object") {
            paths.add(path);
        }
        if (prop.children) {
            for (const childPath of collectLeafSchemaPaths(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

/**
 * 1. Drops entries whose target path no longer exists in the target schema.
 * 2. Removes source-path references that no longer exist in the source schema.
 * Returns the cleaned mappings, or `null` if nothing changed.
 */
export function cleanStaleMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    const validSourcePaths = collectSchemaPaths(sourceSchema);
    const validTargetPaths = collectSchemaPaths(targetSchema);
    let hasStale = false;
    const cleaned: Record<string, VdmMappingEntry> = {};

    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (!validTargetPaths.has(targetPath)) {
            hasStale = true;
            continue;
        }

        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length === 0) {
            cleaned[targetPath] = entry;
            continue;
        }

        const stalePaths = sourcePaths.filter(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)));
        if (stalePaths.length === 0) {
            cleaned[targetPath] = entry;
            continue;
        }

        hasStale = true;
        let newExpression = entry.value;
        for (const stalePath of stalePaths) {
            newExpression = removeSourcePath(newExpression, stalePath, entry.mode);
        }
        if (newExpression.trim()) {
            cleaned[targetPath] = { ...entry, value: newExpression };
        }
    }

    return hasStale ? cleaned : null;
}

/**
 * Forces csharp mode for any text-mode mapping whose target is now a non-string type, since text mode
 * emits a handlebars string that fails strict validation against a non-string target. A pure direct
 * mapping is converted to its csharp form; composite expressions keep their value and are flagged as
 * broken for the user to fix. Returns the updated mappings, or null if nothing changed.
 */
export function normalizeMappingModes(
    mappingExpressions: Record<string, VdmMappingEntry>,
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    let changed = false;
    const updated: Record<string, VdmMappingEntry> = {};
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        const targetType = findSchemaType(targetSchema, targetPath);
        const correctedMode = targetType ? resolveEditorMode(entry.mode, targetType) : entry.mode;
        if (correctedMode === entry.mode) {
            updated[targetPath] = entry;
            continue;
        }
        const convertedValue = correctedMode === "csharp" ? directTextMappingToCsharp(entry.value) : null;
        updated[targetPath] = { ...entry, mode: correctedMode, value: convertedValue ?? entry.value };
        changed = true;
    }
    return changed ? updated : null;
}

export interface RemoveEdgeMappingsResult {
    // Updated mappings, or null when nothing was removed.
    mappings: Record<string, VdmMappingEntry> | null;
    // True when a reference to the clicked source would survive the removal, because the
    // expression around it is a custom one (a helper block, a composite, or a method argument
    // carrying another reference). A declined entry is left completely untouched, its surviving
    // reference redraws the edge, and the user needs telling why.
    declined: boolean;
}

/**
 * When a user clicks an edge to delete it, the edge might represent a collapsed node
 * so check through the mapping expressions for any references to the source path and remove them.
 */
export function removeEdgeMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourcePath: string,
    targetPath: string,
    sourceSchema?: JsonSchemaProperty[],
    targetSchema?: JsonSchemaProperty[],
): RemoveEdgeMappingsResult {
    const updated = { ...mappingExpressions };
    let changed = false;
    let declined = false;
    const targetSegs = resolvePathSegments(targetPath, targetSchema);
    const sourceSegs = resolvePathSegments(sourcePath, sourceSchema);
    const isUnderClickedSource = (sp: string) => isSegmentPrefix(sourceSegs, resolvePathSegments(sp, sourceSchema));

    for (const [tPath, entry] of Object.entries(updated)) {
        if (!isSegmentPrefix(targetSegs, resolvePathSegments(tPath, targetSchema))) {
            continue;
        }

        const matching = extractSourcePaths(entry.value, entry.mode).filter(isUnderClickedSource);
        if (matching.length === 0) {
            continue;
        }

        let newExpression = entry.value;
        for (const sp of matching) {
            newExpression = removeSourcePath(newExpression, sp, entry.mode);
        }

        // removeSourcePath leaves custom expressions untouched rather than mangling them. Decline
        // is judged by what survived, not by what changed: a partial removal can delete one token
        // yet keep another ref to the clicked source (e.g. in a helper block, or a second
        // occurrence of the same path inside a composite), and any surviving ref redraws the edge.
        // A declined entry is left completely untouched - removal is all-or-nothing per entry.
        if (extractSourcePaths(newExpression, entry.mode).some(isUnderClickedSource)) {
            declined = true;
            continue;
        }

        if (newExpression.trim()) {
            updated[tPath] = { ...entry, value: newExpression };
        } else {
            delete updated[tPath];
        }
        changed = true;
    }

    return { mappings: changed ? updated : null, declined };
}

export function expressionToTokens(expression: string, validSourcePaths?: Set<string>, mode: VdmExpressionMode = "text"): MappedValueToken[] {
    const sourcePaths = extractSourcePaths(expression, mode);

    if (sourcePaths.length === 0) {
        const isInvalid = (mode === "text" && hasBareReference(expression))
            || (mode === "csharp" && hasHandlebarsReference(expression));
        return [{ text: expression, variant: isInvalid ? "invalid" : "static" }];
    }

    const hasInvalidPath = (mode === "text" && hasBareReference(expression))
        || (mode === "csharp" && hasHandlebarsReference(expression))
        || (validSourcePaths != null && sourcePaths.some(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p))));

    // Direct mapping: expression is exactly one source ref
    if (mode === "csharp") {
        const refs = collectSourceRefs(expression, mode);
        const isDirect = refs.length === 1 && refs[0].start === 0 && refs[0].tokenEnd === expression.length;
        if (isDirect) {
            return [{ text: `...${lastPathSegment(sourcePaths[0])}`, variant: hasInvalidPath ? "invalid" : "direct" }];
        }
    } else {
        const singleSourceToken = sourcePaths.length === 1 ? formatSourceToken(sourcePaths[0], mode) : null;
        if (singleSourceToken && expression === singleSourceToken) {
            return [{ text: `{{...${lastPathSegment(sourcePaths[0])}}}`, variant: hasInvalidPath ? "invalid" : "direct" }];
        }
    }

    // Modified: replace #source.path with ...lastSegment, keep everything else as-is
    const display = replaceSourceRefs(expression, mode);
    return [{ text: display, variant: hasInvalidPath ? "invalid" : "modified" }];
}

function replaceSourceRefs(expression: string, mode: VdmExpressionMode): string {
    const refs = collectSourceRefs(expression, mode);
    let result = expression;
    for (const ref of [...refs].sort((a, b) => b.start - a.start)) {
        result = result.substring(0, ref.start) + `...${lastPathSegment(ref.path)}` + result.substring(ref.tokenEnd);
    }
    return result;
}

function lastPathSegment(path: string): string {
    return path ? (path.split(".").pop() ?? path) : "#source";
}

export function expressionHasInvalidSourcePaths(expression: string, validSourcePaths: Set<string>, mode: VdmExpressionMode = "text"): boolean {
    const sourcePaths = extractSourcePaths(expression, mode);
    return sourcePaths.some(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)))
        || (mode === "text" && hasBareReference(expression))
        || (mode === "csharp" && hasHandlebarsReference(expression));
}

function flattenProperty(
    prop: JsonSchemaProperty,
    side: "source" | "target",
    connectedIds: Set<string>,
    depth: number,
    parentPath: string,
    rowCounter: { value: number },
    parentNodeId: string,
    panelWidth: number,
    mappingExpressions?: Record<string, VdmMappingEntry>,
    targetTokenResolver?: (targetPath: string, expression: string) => MappedValueToken[],
    blockedTargetPaths?: Set<string>,
): Node[] {
    const nodes: Node[] = [];
    const path = buildSchemaPath(parentPath, prop);
    const childParentPath = path;
    const nodeId = `${side}-${path}`;
    const resolveTokens = targetTokenResolver ?? ((_p: string, expr: string) => expressionToTokens(expr));

    const isExpandable = prop.type === "object" || (prop.type === "array" && !!prop.children?.length);
    // Show handles on array nodes so the whole array can be mapped while expanded.
    const hasHandle = !isExpandable || !!prop.isCollapsed || prop.type === "array";
    const baseIsConnectable = hasHandle && prop.type !== "object";
    const isBlocked = side === "target" && !!blockedTargetPaths?.has(path);
    const isConnectable = baseIsConnectable && !isBlocked;

    const nodeWidth = panelWidth - CONTENT_PADDING * 2;
    const openBracket = isExpandable
        ? (prop.type === "array" ? "[" : "{")
        : undefined;
    const closeBracket = isExpandable
        ? (prop.type === "array" ? "]" : "}")
        : undefined;

    const mappingEntry = side === "target" && mappingExpressions ? mappingExpressions[path] : undefined;

    const nodeData: JsonPropertyNodeData = {
        propertyKey: prop.key,
        valueType: prop.isCollapsed && prop.collapsedDisplayType
            ? prop.collapsedDisplayType
            : prop.displayType ?? (isExpandable ? "" : prop.type),
        depth,
        side,
        isExpandable,
        isExpanded: !prop.isCollapsed,
        hasHandle,
        isConnected: connectedIds.has(nodeId),
        isConnectable,
        nodePath: path,
        collapsedPreview: prop.isCollapsed
            ? (!prop.displayType && !prop.collapsedDisplayType ? (prop.type === "array" ? "[]" : "{}") : undefined)
            : undefined,
        openBracket: (!prop.isCollapsed && isExpandable) ? openBracket : undefined,
        mappedTokens: mappingEntry
            ? resolveTokens(path, mappingEntry.value)
            : undefined,
        mappingExpression: mappingEntry?.value,
        targetPath: side === "target" ? path : undefined,
        targetType: side === "target" ? prop.type : undefined,
        isBlocked: side === "target" ? isBlocked : undefined,
        baseIsConnectable: side === "target" ? baseIsConnectable : undefined,
    };

    nodes.push({
        id: nodeId,
        type: "jsonProperty",
        position: { x: CONTENT_PADDING, y: CONTENT_PADDING + rowCounter.value * ROW_HEIGHT },
        data: nodeData,
        parentId: parentNodeId,
        extent: "parent" as const,
        draggable: false,
        style: { width: nodeWidth },
    });

    rowCounter.value++;

    if (prop.children && !prop.isCollapsed) {
        for (const child of prop.children) {
            nodes.push(...flattenProperty(child, side, connectedIds, depth + 1, childParentPath, rowCounter, parentNodeId, panelWidth, mappingExpressions, targetTokenResolver, blockedTargetPaths));
        }

        // Closing bracket row
        if (closeBracket) {
            nodes.push({
                id: `${nodeId}-close`,
                type: "jsonProperty",
                position: { x: CONTENT_PADDING, y: CONTENT_PADDING + rowCounter.value * ROW_HEIGHT },
                data: {
                    propertyKey: closeBracket,
                    valueType: "",
                    depth,
                    side,
                    isExpandable: false,
                    isExpanded: false,
                    hasHandle: false,
                    isConnected: false,
                    isConnectable: false,
                    isBracket: true,
                } satisfies JsonPropertyNodeData,
                parentId: parentNodeId,
                extent: "parent" as const,
                draggable: false,
                style: { width: nodeWidth },
            });
            rowCounter.value++;
        }
    }

    return nodes;
}

export function generateInitialNodes(
    containerWidth: number,
    sourceSchema?: JsonSchemaProperty[],
    targetSchema?: JsonSchemaProperty[],
    connectedEdges?: Edge[],
    mappingExpressions?: Record<string, VdmMappingEntry>,
    targetTokenResolver?: (targetPath: string, expression: string) => MappedValueToken[],
): { nodes: Node[]; maxHeight: number } {
    const DEFAULT_PANEL_HEIGHT = 200;
    const panelWidth = (containerWidth - PANEL_GAP) / 2;

    const connectedSourceIds = new Set((connectedEdges ?? []).map((e) => e.source));
    const connectedTargetIds = new Set((connectedEdges ?? []).map((e) => e.target));

    // Mark targets with source references as connected (static-value-only targets should remain unconnected).
    if (mappingExpressions) {
        for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
            if (extractSourcePaths(entry.value, entry.mode).length > 0) {
                connectedTargetIds.add(`target-${targetPath}`);
            }
        }
    }

    // Flatten property nodes and compute panel heights
    const sourceNodes: Node[] = [];
    let sourcePanelHeight = 0;
    let sourceData: Record<string, unknown> = { emptyMessage: "No source data selected." };

    if (sourceSchema && sourceSchema.length > 0) {
        const sourceRowCounter = { value: 0 };
        for (const prop of sourceSchema) {
            sourceNodes.push(...flattenProperty(prop, "source", connectedSourceIds, 0, "", sourceRowCounter, "source-panel", panelWidth));
        }
        sourcePanelHeight = CONTENT_PADDING * 2 + sourceRowCounter.value * ROW_HEIGHT;
        sourceData = {};
    }

    const targetNodes: Node[] = [];
    let targetPanelHeight = 0;
    let targetData: Record<string, unknown> = { emptyMessage: "No target schema defined." };

    if (targetSchema && targetSchema.length > 0) {
        const targetRowCounter = { value: 0 };
        const blockedTargetPaths = findBlockedTargetPaths(targetSchema, mappingExpressions);
        for (const prop of targetSchema) {
            targetNodes.push(...flattenProperty(prop, "target", connectedTargetIds, 0, "", targetRowCounter, "target-panel", panelWidth, mappingExpressions, targetTokenResolver, blockedTargetPaths));
        }
        targetPanelHeight = CONTENT_PADDING * 2 + targetRowCounter.value * ROW_HEIGHT;
        targetData = {};
    }

    // Use the same height for both panels
    const effectiveSourcePanelHeight = sourcePanelHeight || DEFAULT_PANEL_HEIGHT;
    const effectiveTargetPanelHeight = targetPanelHeight || DEFAULT_PANEL_HEIGHT;
    const maxHeight = Math.max(effectiveSourcePanelHeight, effectiveTargetPanelHeight);

    // Create panel frame nodes with equal heights, then append children
    // Parent nodes must appear before their children in the array
    const allNodes: Node[] = [
        {
            id: "source-panel",
            type: "panelFrame",
            position: { x: 0, y: 0 },
            data: sourceData,
            draggable: false,
            style: { width: panelWidth, height: maxHeight },
        },
        ...sourceNodes,
        {
            id: "target-panel",
            type: "panelFrame",
            position: { x: panelWidth + PANEL_GAP, y: 0 },
            data: targetData,
            draggable: false,
            style: { width: panelWidth, height: maxHeight },
        },
        ...targetNodes,
    ];

    return { nodes: allNodes, maxHeight };
}

// Used for getting the up-to-date display label (#var for variables, #result for everything else) for the source shape in the VDM editor
export function resolveSourceLabel(sourceElementId: string, elements: IElementDictionary, variableSchemas?: Record<string, string>): string {
    if (!sourceElementId) {
        return "";
    }
    const el = elements[sourceElementId];
    if (!el) {
        if (variableSchemas && Object.hasOwn(variableSchemas, sourceElementId)) {
            return `#var.${sourceElementId}`;
        }
        return sourceElementId;
    }

    if (el.type === ElementType.Expression) {
        const hasAssignedVariable = el.parameters?.shouldAssignVariable as { value: boolean | string } | undefined;

        if (hasAssignedVariable && (hasAssignedVariable.value === true || hasAssignedVariable.value === "true")) {
            const varName = el.parameters?.variableName as string | undefined;
            if (varName) {
                return `#var.${varName}`;
            }
        }
    }

    if (isForeach(el)) {
        const iterationVariable = getForeachVariableName(el);
        return iterationVariable ? `#var.${iterationVariable}` : sourceElementId;
    }

    return el.name ? `#result[${el.name}]` : sourceElementId;
}

/**
 * Given a node path (e.g. "user.profile.bio") and a set of collapsed paths,
 * returns the first visible ancestor node ID, or the original path if not collapsed.
 * A node is considered collapsed-hidden if any ancestor path is in the collapsed set.
 */
export function getVisibleAncestor(path: string, side: "source" | "target", collapsedPaths: Set<string>): string {
    let currentPath = path;
    let outermost: string | undefined;

    while (true) {
        if (collapsedPaths.has(currentPath)) {
            outermost = currentPath;
        }

        const cutIndex = currentPath.lastIndexOf(".");

        if (cutIndex === -1) {
            break;
        }

        currentPath = currentPath.slice(0, cutIndex);
    }

    return outermost !== undefined ? `${side}-${outermost}` : `${side}-${path}`;
}

/**
 * Remap edges to account for collapsed nodes.
 * - If a node is inside a collapsed parent, the edge is remapped to the collapsed ancestor.
 * - Duplicate remapped edges are merged (only the first occurrence is kept).
 */
export function remapEdgesForCollapsedNodes(
    edges: Edge[],
    collapsedSourcePaths: Set<string>,
    collapsedTargetPaths: Set<string>,
): Edge[] {
    const seen = new Set<string>();
    const result: Edge[] = [];

    for (const edge of edges) {
        const sourcePath = edge.source.replace(/^source-/, "");
        const targetPath = edge.target.replace(/^target-/, "");

        const mappedSource = getVisibleAncestor(sourcePath, "source", collapsedSourcePaths);
        const mappedTarget = getVisibleAncestor(targetPath, "target", collapsedTargetPaths);

        const edgeKey = `${mappedSource}->${mappedTarget}`;
        if (seen.has(edgeKey)) {
            continue;
        }
        seen.add(edgeKey);

        result.push({
            ...edge,
            id: edgeKey,
            source: mappedSource,
            target: mappedTarget,
        });
    }

    return result;
}

/**
 * Apply collapsed paths to schema properties before flattening.
 * Returns a new schema array with isCollapsed set based on the collapsed paths set.
 */
export function applyCollapsedState(
    schema: JsonSchemaProperty[],
    collapsedPaths: Set<string>,
    parentPath: string = "",
): JsonSchemaProperty[] {
    return schema.map((prop) => {
        const path = buildSchemaPath(parentPath, prop);
        const isExpandable = prop.type === "object" || (prop.type === "array" && !!prop.children?.length);
        const isCollapsed = isExpandable && collapsedPaths.has(path);

        return {
            ...prop,
            isCollapsed,
            children: prop.children && !isCollapsed
                ? applyCollapsedState(prop.children, collapsedPaths, path)
                : prop.children,
        };
    });
}



export function computeMappingWarning(expression: string, targetType: string, sourceSchema: JsonSchemaProperty[], envVarTypeMap: Record<string, string> = {}, mode: VdmExpressionMode = "text"): string | null {
    const sourcePaths = extractSourcePaths(expression, mode);
    const envPaths = extractEnvPaths(expression, mode);

    if ((sourcePaths.length === 0 && envPaths.length === 0) || !targetType) {
        return null;
    }

    const totalRefs = sourcePaths.length + envPaths.length;
    if (totalRefs > 1) {
        return targetType === "string"
            ? "Multiple references are mapped to a single target. Ensure that the expression produces the desired string."
            : `Multiple references are mapped to a single target. Ensure that the expression produces a valid "${targetType}".`;
    }

    // Single source ref
    if (sourcePaths.length === 1) {
        const expectedDirectMapping = formatSourceToken(sourcePaths[0], mode);
        if (expression.trim() !== expectedDirectMapping) {
            return null;
        }
        const sourceType = findSchemaType(sourceSchema, sourcePaths[0]);
        if (sourceType && sourceType !== targetType) {
            return `Source type "${sourceType}" does not match target type "${targetType}". The value may need conversion.`;
        }
    }

    // Single env ref
    if (envPaths.length === 1) {
        const expectedDirectMapping = mode === "csharp"
            ? `#env.${envPaths[0]}`
            : `{{#env.${envPaths[0]}}}`;
        if (expression.trim() !== expectedDirectMapping) {
            return null;
        }
        const envType = envVarTypeMap[envPaths[0]];
        if (envType && !areTypesCompatible(envType, targetType)) {
            return `Environment variable type "${toJsonSchemaType(envType)}" does not match target type "${targetType}". The value may need conversion.`;
        }
    }

    return null;
}

// Maps backend EnvironmentVariableType (PascalCase) to JSON Schema type (lowercase).
function toJsonSchemaType(envVarType: string): string {
    if (envVarType === "Secret") {
        return "string";
    }
    return envVarType.toLowerCase();
}

function areTypesCompatible(envSchemaType: string, targetType: string): boolean {
    const envJsonType = toJsonSchemaType(envSchemaType);
    if (envJsonType === targetType) {
        return true;
    }
    // Number env vars store integers, compatible with both JSON Schema "number" and "integer"
    if (envJsonType === "number" && targetType === "integer") {
        return true;
    }
    return false;
}

/**
 * Detects mapping entries where the schema types changed since the mapping was saved,
 * causing a type mismatch. For mappings with stored types, only flags when the stored
 * types differ from current (i.e. the schema changed after the connection was drawn).
 * Legacy mappings without stored types flag any mismatch as a fallback.
 */
export function detectTypeMismatchPaths(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Set<string> {
    const mismatched = new Set<string>();
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        const targetType = findSchemaType(targetSchema, targetPath);
        if (!targetType) {
            continue;
        }
        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length !== 1) {
            continue;
        }
        const sourceType = findSchemaType(sourceSchema, sourcePaths[0]);
        if (!sourceType || sourceType === targetType) {
            continue;
        }
        // Current types are mismatched. Check whether the schema changed:
        // - Legacy mappings (no stored types): flag any mismatch as fallback
        // - New mappings (stored types): only flag if stored types differ from current
        if (!entry.sourceType && !entry.targetType) {
            mismatched.add(targetPath);
        } else if (entry.sourceType !== sourceType || entry.targetType !== targetType) {
            mismatched.add(targetPath);
        }
    }
    return mismatched;
}

/** Warning shown for mappings that read through a source array; the runtime has no implicit
 * flattening, so such a read fails on an array of any length, one element included. The flatten
 * remedy carries casts because a source reference is dynamic, and a lambda argument to a
 * dynamically dispatched call does not compile. */
export const ARRAY_CROSSING_WARNING =
    "The source path passes through an array, so it cannot be read as a single value: the mapping "
    + "fails at runtime whatever the array's length. Pick one element with an "
    + "index (e.g. items[0]), map a field inside an array target, or flatten the elements in "
    + "Expression mode, casting each level (e.g. ((IEnumerable<dynamic>)items).SelectMany(i => "
    + "(IEnumerable<dynamic>)i.tags)) - without the casts the expression does not compile.";

/**
 * Detects mapping entries whose source path reads THROUGH a source array without consuming it:
 * an un-indexed array segment the path continues past, on a mapping whose target is not inside an
 * array scope. Such a read compiles silently but fails at runtime, so the mapper warns at design time.
 */
export function detectArrayCrossingPaths(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Set<string> {
    const flagged = new Set<string>();
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (mappingCrossesSourceArray(entry.value, entry.mode, targetPath, sourceSchema, targetSchema)) {
            flagged.add(targetPath);
        }
    }
    return flagged;
}

/**
 * Whether one mapping expression reads through a source array it does not consume. Each array scope
 * enclosing the target binds exactly one crossing as its loop driver, so a chain crossing more
 * arrays than there are enclosing scopes leaves the surplus rooted at the source and failing at
 * runtime - which is what a target outside any scope (no drivers at all) is the simplest case of.
 */
export function mappingCrossesSourceArray(
    expression: string,
    mode: VdmExpressionMode,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): boolean {
    if (sourceSchema.length === 0) {
        return false;
    }
    const boundCrossings = countTargetArrayScopes(targetSchema, targetPath);
    if (boundCrossings === null) {
        return false;
    }
    return extractSourcePaths(expression, mode)
        .some((path) => countSourceArrayCrossings(sourceSchema, path) > boundCrossings);
}

/** Warning shown for mappings inside an array scope that index only some dimensions of a nested
 * source array. The bound array follows the iteration, so the indexer applies within each iterated
 * element - a coherent meaning, but easy to misread as selecting a row up front. */
export const PARTIAL_INDEX_WARNING =
    "The index covers only some levels of this nested array. Inside an array mapping the reference "
    + "follows the iteration, so the index picks within each iterated element instead of selecting "
    + "one row up front. Index every level (e.g. matrix[0][0]) to pin a single value. Dropping the "
    + "index iterates all elements, but the mapping is then accepted only when the source array's "
    + "nesting depth matches the target array's.";

/**
 * Detects mapping entries inside an array scope whose source path partially indexes a nested
 * array (see PARTIAL_INDEX_WARNING).
 */
export function detectPartialIndexPaths(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Set<string> {
    const flagged = new Set<string>();
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (mappingPartiallyIndexesBoundArray(entry.value, entry.mode, targetPath, sourceSchema, targetSchema)) {
            flagged.add(targetPath);
        }
    }
    return flagged;
}

/**
 * Whether one mapping inside an array scope indexes fewer dimensions of a nested source array than
 * it has. Outside a scope the same read either selects a row whole (valid) or continues into
 * element fields and fails at runtime - the crossing warning's territory, which also outranks this
 * advisory when a read does both.
 */
export function mappingPartiallyIndexesBoundArray(
    expression: string,
    mode: VdmExpressionMode,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): boolean {
    if (sourceSchema.length === 0) {
        return false;
    }
    const boundCrossings = countTargetArrayScopes(targetSchema, targetPath);
    if (boundCrossings === null || boundCrossings === 0) {
        return false;
    }
    if (mappingCrossesSourceArray(expression, mode, targetPath, sourceSchema, targetSchema)) {
        return false;
    }
    return extractSourcePaths(expression, mode).some((path) => {
        const segments = path.replace(/^\.+/, "").split(".").filter((s) => s !== "");
        // Only the chain prefix up to the first indexer can rebind to the iteration - an indexer
        // ends the backend's chain, so arrays behind it stay rooted at the source.
        const first = walkSchemaChain(sourceSchema, segments)
            .find((entry) => entry.indexerCount === undefined || entry.indexerCount > 0);
        return first !== undefined
            && first.node.type === "array"
            && first.indexerCount !== undefined
            && first.indexerCount < resolveArrayDepth(first.node);
    });
}

/** How many array scopes enclose the target path, one driver bound per scope. Counts array nodes
 * rather than dimensions: a flattened array-of-arrays node is one scope, and the backend requires
 * its driver to match that node's whole depth. Resolution matches whole schema keys, so a key
 * containing a dot addresses one property rather than a nested path. Null when the path resolves to
 * no node (stale mapping, owned by stale-mapping validation) or to several (a dotted key colliding
 * with a nested path, which the backend rejects outright), and the warning is then suppressed. */
function countTargetArrayScopes(targetSchema: JsonSchemaProperty[], targetPath: string): number | null {
    const matches: JsonSchemaProperty[][] = [];
    matchPathNodes(targetSchema, normalizePathForSchema(targetPath), [], matches);
    if (matches.length !== 1) {
        return null;
    }
    return matches[0].slice(0, -1).filter((node) => node.type === "array").length;
}

/** How many source arrays the path reads through without consuming them. An array the path ends on
 * is consumed whole; an indexed one is consumed only once the indexers reach its nesting depth,
 * since nested arrays collapse onto a single node and one indexer peels one dimension. Only a
 * schema-declared continuation counts, so an undeclared tail on the array itself is not a crossing -
 * it may be a runtime member (e.g. items.Count). */
function countSourceArrayCrossings(sourceSchema: JsonSchemaProperty[], rawPath: string): number {
    const segments = rawPath.replace(/^\.+/, "").split(".").filter((s) => s !== "");
    const chain = walkSchemaChain(sourceSchema, segments);
    return chain.slice(0, -1)
        .filter((entry) => entry.node.type === "array"
            && entry.indexerCount !== undefined
            && entry.indexerCount < resolveArrayDepth(entry.node))
        .length;
}

/** An indexerCount of undefined means the segment's indexer is not an array index (a quoted
 * dictionary key), so how many dimensions it consumes is unknown and no crossing is reported. */
type SchemaChainEntry = { node: JsonSchemaProperty; indexerCount: number | undefined };

/** Schema nodes matched along the segments of a source path, with per-segment indexer info. Walks
 * from the first segment and stops at the first miss, so a partly declared path still reports the
 * prefix it resolved - an undeclared tail is a runtime member, not a crossing. Segments match on the
 * same key the schema paths are built from (pathSegment ?? key), with indexers stripped for the
 * lookup. Target paths use matchPathNodes instead, which matches whole keys and needs no prefixes. */
function walkSchemaChain(
    schema: JsonSchemaProperty[],
    segments: string[],
): SchemaChainEntry[] {
    const chain: SchemaChainEntry[] = [];
    let nodes: JsonSchemaProperty[] | undefined = schema;
    for (const segment of segments) {
        const key = segment.replace(/\[[^\]]*\]/g, "");
        let node: JsonSchemaProperty | undefined = nodes?.find((n) => (n.pathSegment ?? n.key) === key);
        if (!node && key !== "") {
            // A root-level array is a transparent wrapper (empty path segment) whose children sit
            // at its own path level, so a named segment resolves one level down. The wrapper still
            // enters the chain: un-indexed, it is precisely the array the read passes through.
            const wrapper: JsonSchemaProperty | undefined = nodes?.find((n) => (n.pathSegment ?? n.key) === "");
            const nested: JsonSchemaProperty | undefined = wrapper?.children?.find((n) => (n.pathSegment ?? n.key) === key);
            if (wrapper && nested) {
                chain.push({ node: wrapper, indexerCount: 0 });
                node = nested;
            }
        }
        if (!node) {
            return chain;
        }
        chain.push({ node, indexerCount: trailingIndexerCount(segment) });
        nodes = node.children;
    }
    return chain;
}

function normalizeScalarType(type: string): string {
    return type === "integer" ? "number" : type;
}

const PRIMITIVE_ARRAY_DISPLAY_TYPE = /^(string|number|integer|boolean)(?:\[\])+$/;

/** Item type of a primitive array, the one piece of structure only `displayType` records; undefined when the items are structured or unknown. */
function primitiveArrayItemType(prop: JsonSchemaProperty): string | undefined {
    return PRIMITIVE_ARRAY_DISPLAY_TYPE.exec(prop.displayType ?? "")?.[1];
}

/** Element type + depth of a primitive array (e.g. "number[]", "number[][]"), or undefined if the array's element type is unknown or structured. */
function primitiveArrayElementDescriptor(prop: JsonSchemaProperty): string | undefined {
    const itemType = primitiveArrayItemType(prop);
    return itemType ? `${normalizeScalarType(itemType)}${"[]".repeat(resolveArrayDepth(prop))}` : undefined;
}

/**
 * Whether a source value can be assigned directly to a target field without conversion. Recursively
 * compares structure so array element types and nested object-array item shapes are checked, not just
 * the top-level JSON Schema type (both `string[]` and `number[]` are "array"). Permissive when a type
 * is unknown, to avoid dropping mappings whose compatibility cannot be proven.
 */
function arePropsTypeCompatible(source: JsonSchemaProperty, target: JsonSchemaProperty): boolean {
    const sourceType = normalizeScalarType(source.type);
    const targetType = normalizeScalarType(target.type);
    // Untyped schema nodes (no type/properties/items) resolve to "". Treat unknown types as permissive
    // rather than dropping mappings whose compatibility cannot be proven.
    if (!sourceType || !targetType) {
        return true;
    }
    if (sourceType !== targetType) {
        return false;
    }
    if (sourceType === "array") {
        return areArrayItemsCompatible(source, target);
    }
    if (sourceType === "object") {
        return areObjectChildrenCompatible(source.children, target.children);
    }
    return true;
}

function areArrayItemsCompatible(source: JsonSchemaProperty, target: JsonSchemaProperty): boolean {
    const sourceHasObjectItems = !!source.children?.length;
    const targetHasObjectItems = !!target.children?.length;
    if (sourceHasObjectItems || targetHasObjectItems) {
        // Object-item arrays must have matching nesting depth and compatible item object shapes.
        if (!sourceHasObjectItems || !targetHasObjectItems || resolveArrayDepth(source) !== resolveArrayDepth(target)) {
            return false;
        }
        return areObjectChildrenCompatible(source.children, target.children);
    }
    // Primitive item arrays: compare normalised element descriptors when both are known.
    const sourceItem = primitiveArrayElementDescriptor(source);
    const targetItem = primitiveArrayElementDescriptor(target);
    if (!sourceItem || !targetItem) {
        return true;
    }
    return sourceItem === targetItem;
}

/** Compares overlapping object fields by key; only fields present in both sides must be type-compatible. */
function areObjectChildrenCompatible(
    sourceChildren: JsonSchemaProperty[] | undefined,
    targetChildren: JsonSchemaProperty[] | undefined,
): boolean {
    if (!sourceChildren?.length || !targetChildren?.length) {
        return true;
    }
    const targetByKey = new Map(targetChildren.map((child) => [child.key, child]));
    for (const sourceChild of sourceChildren) {
        const targetChild = targetByKey.get(sourceChild.key);
        if (targetChild && !arePropsTypeCompatible(sourceChild, targetChild)) {
            return false;
        }
    }
    return true;
}

const TRAILING_INDEXERS = /(?:\[[^\]]*\])+$/;

/** Array indices applied to the value a path addresses ("roles[0]" -> 1), or undefined when an indexer is not an array index (a quoted dictionary key). */
function trailingIndexerCount(path: string): number | undefined {
    let bracketDepth = 0;
    let segmentStart = 0;
    for (let i = path.length - 1; i >= 0; i--) {
        const char = path[i];
        if (char === "]") {
            bracketDepth++;
        } else if (char === "[") {
            bracketDepth--;
        } else if (char === "." && bracketDepth === 0) {
            segmentStart = i + 1;
            break;
        }
    }

    const indexers = TRAILING_INDEXERS.exec(path.substring(segmentStart));
    if (!indexers) {
        return 0;
    }
    // C# allows whitespace inside an indexer, and the expression parser keeps the brackets verbatim.
    const parts = indexers[0].slice(1, -1).split("][");
    return parts.every(part => /^\d+$/.test(part.trim())) ? parts.length : undefined;
}

/** The property an indexed element addresses, one array dimension peeled per index. Undefined when the item type is unknown or the path over-indexes, so callers stay permissive rather than call it incompatible. */
function indexedElementProperty(prop: JsonSchemaProperty, indexerCount: number): JsonSchemaProperty | undefined {
    if (prop.type !== "array") {
        return undefined;
    }

    const remainingDepth = resolveArrayDepth(prop) - indexerCount;
    if (remainingDepth < 0) {
        return undefined;
    }

    if (prop.children?.length) {
        return remainingDepth === 0
            ? { key: prop.key, type: "object", children: prop.children }
            : { key: prop.key, type: "array", arrayDepth: remainingDepth, children: prop.children };
    }

    const itemType = primitiveArrayItemType(prop);
    if (!itemType) {
        return undefined;
    }
    return remainingDepth === 0
        ? { key: prop.key, type: itemType }
        : { key: prop.key, type: "array", arrayDepth: remainingDepth, displayType: `${itemType}${"[]".repeat(remainingDepth)}` };
}

/** Resolves a path to the property it addresses: indexers on intermediate segments are dropped, trailing ones peel array dimensions ("roles[0]" on a string[] is its string element). */
function findSchemaProperty(schema: JsonSchemaProperty[], path: string): JsonSchemaProperty | undefined {
    const prop = findSchemaPropertyRecursive(schema, normalizePathForSchema(path), "");
    if (!prop) {
        return undefined;
    }
    const indexerCount = trailingIndexerCount(path);
    if (indexerCount === undefined) {
        return undefined;
    }
    return indexerCount === 0 ? prop : indexedElementProperty(prop, indexerCount);
}

function findSchemaPropertyRecursive(schema: JsonSchemaProperty[], normalizedPath: string, parentPath: string): JsonSchemaProperty | undefined {
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        if (path === normalizedPath) {
            return prop;
        }
        if (prop.children) {
            const child = findSchemaPropertyRecursive(prop.children, normalizedPath, path);
            if (child) {
                return child;
            }
        }
    }
    return undefined;
}

/**
 * Reduces a source path as written in an expression to the schema path it addresses, by dropping
 * every indexer segment. Schema paths carry no indexers, so both array indices ("items[0].sku" ->
 * "items.sku") and quoted keys ('dict["foo"].bar' -> "dict.bar") are stripped, including root-indexed
 * paths ("[0].name" -> "name"). Bracket-free paths are returned unchanged, so this is safe (and a
 * no-op) on target paths and already-normalized paths.
 *
 * Call this before comparing or looking up an extracted source path against schema-derived data.
 */
export function normalizePathForSchema(path: string): string {
    return path
        .replace(/\[[^\]]*\]/g, "")
        .replace(/^\.+/, "")
        .replace(/\.{2,}/g, ".");
}

/** Whether the csharp expression is a bare direct reference to a single source field (e.g. "#source.tags", "#source[0].name"), as opposed to a composite or transforming expression. */
function isDirectCsharpSourceMapping(value: string, path: string): boolean {
    return value.trim() === formatSourceToken(path, "csharp");
}

/**
 * Whether a mapping is a direct, value-preserving assignment of a single source field whose type is
 * incompatible with the target field. Only csharp-mode direct references are checked: text mode
 * stringifies its value, and composite/transforming expressions are intentional and not validated here.
 */
function isTypeIncompatibleDirectMapping(
    entry: VdmMappingEntry,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): boolean {
    if (entry.mode !== "csharp") {
        return false;
    }
    const sourcePaths = extractSourcePaths(entry.value, entry.mode);
    if (sourcePaths.length !== 1 || !isDirectCsharpSourceMapping(entry.value, sourcePaths[0])) {
        return false;
    }
    const sourceProp = findSchemaProperty(sourceSchema, sourcePaths[0]);
    const targetProp = findSchemaProperty(targetSchema, targetPath);
    if (!sourceProp || !targetProp) {
        return false;
    }
    return !arePropsTypeCompatible(sourceProp, targetProp);
}

/**
 * Removes mappings whose source and target field types are incompatible (e.g. a `string[]` source
 * mapped onto a `number[]` target), preventing silent runtime failures under strict schema
 * validation. One step of `sanitizeAssistantMappings`, kept separate because type compatibility is
 * its own concern. Returns the surviving mappings and the target paths that were dropped.
 */
export function filterTypeIncompatibleMappings(
    mappings: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): { mappings: Record<string, VdmMappingEntry>; droppedTargetPaths: string[] } {
    const surviving: Record<string, VdmMappingEntry> = {};
    const droppedTargetPaths: string[] = [];
    for (const [targetPath, entry] of Object.entries(mappings)) {
        if (isTypeIncompatibleDirectMapping(entry, targetPath, sourceSchema, targetSchema)) {
            droppedTargetPaths.push(targetPath);
        } else {
            surviving[targetPath] = entry;
        }
    }
    return { mappings: surviving, droppedTargetPaths };
}

/**
 * The one constant the assistant may produce, and only for a string target. On any other target it
 * is invented data of the kind this sanitiser exists to remove, and nothing downstream would catch
 * it: the type filter only judges values that resolve to a single source path.
 */
const CSHARP_EMPTY_STRING = '""';

function isEmptyStringLiteralOnStringTarget(
    entry: VdmMappingEntry,
    targetPath: string,
    targetSchema: JsonSchemaProperty[],
): boolean {
    return entry.mode === "csharp"
        && entry.value.trim() === CSHARP_EMPTY_STRING
        && findSchemaType(targetSchema, targetPath) === "string";
}

/**
 * Drops the assistant's mappings whose value is a transforming expression rather than a reference,
 * so code the assistant invented never reaches the process compiler. A dropped mapping looks the
 * same as one the assistant declined to make, which is what the prompt asks it to do for any field
 * needing a transformation, so the user fills those in by hand as they would have anyway.
 */
export function sanitizeAssistantMappings(
    mappings: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> {
    const surviving: Record<string, VdmMappingEntry> = {};
    for (const [targetPath, entry] of Object.entries(mappings)) {
        if (isEmptyStringLiteralOnStringTarget(entry, targetPath, targetSchema)
            || isPlainReferenceExpression(entry.value, entry.mode)) {
            surviving[targetPath] = entry;
        }
    }
    return filterTypeIncompatibleMappings(surviving, sourceSchema, targetSchema).mappings;
}

/**
 * Acknowledges a type mismatch by re-stamping the entry's stored source/target types to the
 * current schema types, so detectTypeMismatchPaths no longer flags it. Returns the updated
 * mappings, or null when nothing needs to change (no entry, multi-ref mapping, or already in sync).
 */
export function acknowledgeTypeMismatch(
    mappingExpressions: Record<string, VdmMappingEntry>,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    const entry = mappingExpressions[targetPath];
    if (!entry) {
        return null;
    }
    const sourcePaths = extractSourcePaths(entry.value, entry.mode);
    if (sourcePaths.length !== 1) {
        return null; // only single-source-ref mappings are ever flagged as type mismatches
    }
    const sourceType = findSchemaType(sourceSchema, sourcePaths[0]) || undefined;
    const targetType = findSchemaType(targetSchema, targetPath) || undefined;
    if (entry.sourceType === sourceType && entry.targetType === targetType) {
        return null;
    }
    return { ...mappingExpressions, [targetPath]: { ...entry, sourceType, targetType } };
}

export interface StaleInfo {
    /** Target paths whose source references were removed. Maps targetPath -> the removed source paths */
    sourceRemovedPaths: Map<string, string[]>;
    /** Target paths removed from schema. Maps targetPath -> original mapping entry */
    orphanedTargetExpressions: Record<string, VdmMappingEntry>;
    /** Whether any stale mappings were detected */
    hasStale: boolean;
}

/**
 * Detects which mapping entries have become stale after a schema change.
 * Unlike cleanStaleMappings, this does not modify anything - it only reports.
 */
export function detectStaleMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): StaleInfo {
    const validSourcePaths = collectSchemaPaths(sourceSchema);
    const validTargetPaths = collectSchemaPaths(targetSchema);
    const sourceRemovedPaths = new Map<string, string[]>();
    const orphanedTargetExpressions: Record<string, VdmMappingEntry> = {};

    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (!validTargetPaths.has(targetPath)) {
            orphanedTargetExpressions[targetPath] = entry;
            continue;
        }

        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length === 0) {
            continue;
        }

        const removedSourcePaths = sourcePaths.filter(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)));
        if (removedSourcePaths.length > 0) {
            sourceRemovedPaths.set(targetPath, removedSourcePaths);
        }
    }

    return {
        sourceRemovedPaths,
        orphanedTargetExpressions,
        hasStale: sourceRemovedPaths.size > 0 || Object.keys(orphanedTargetExpressions).length > 0,
    };
}

export function isExpressionAssigningVariable(element: IElement): boolean {
    if (element.type !== ElementType.Expression) {
        return false;
    }

    const shouldAssign = element.parameters?.shouldAssignVariable as { value: boolean | string } | undefined;
    return !!(shouldAssign && (shouldAssign.value === true || shouldAssign.value === "true"));
}

export function getExpressionVariableName(element: IElement): string | undefined {
    return element.parameters?.variableName as string | undefined;
}

export function resolveVdmSourceSchema(
    elements: IElementDictionary,
    variableSchemas: Record<string, string>,
    vdmElement: { parameters?: Record<string, unknown> },
): string | undefined {
    const sourceId = vdmElement.parameters?.sourceNodeId as string | undefined;
    if (!sourceId) {
        return undefined;
    }

    const sourceElement = elements[sourceId];
    return sourceElement ? resolveShapeSchema(sourceElement, variableSchemas) : getVariableSchema(sourceId, variableSchemas);
}

function resolveShapeSchema(element: IElement, variableSchemas: Record<string, string>): string | undefined {
    if (isExpressionAssigningVariable(element)) {
        const varName = getExpressionVariableName(element);
        return varName ? getVariableSchema(varName, variableSchemas) : undefined;
    }

    if (isForeach(element)) {
        return getForeachItemSchema(element);
    }

    return element.parameters?.schema as string | undefined;
}

function getVariableSchema(variableName: string, variableSchemas: Record<string, string>): string | undefined {
    return Object.hasOwn(variableSchemas, variableName) ? variableSchemas[variableName] : undefined;
}

export function isForeach(element: IElement): boolean {
    return element.type === ElementType.SequentialForeach || element.type === ElementType.ParallelForeach;
}

export function getForeachVariableName(element: IElement): string | undefined {
    return element.parameters?.variable as string | undefined;
}

export function getForeachItemSchema(element: IElement): string | undefined {
    return (element.parameters?.[FOREACH_ITEM_SCHEMA_KEY] as string | undefined) || undefined;
}

/** Foreach shapes enclosing the element, innermost first. */
export function getEnclosingLoops(elementId: string, elements: IElementDictionary): EnclosingLoop[] {
    return getEnclosingElementIds(elementId)
        .map((id) => elements[id])
        .filter((element): element is IElement => !!element && isForeach(element))
        .map((foreach) => ({ foreach, variableName: getForeachVariableName(foreach) }))
        .filter((loop): loop is EnclosingLoop => !!loop.variableName);
}

function getEnclosingElementIds(elementId: string): string[] {
    const ids: string[] = [];
    for (let parent = BpmnJsCliWrapper.element(elementId)?.parent; parent; parent = parent.parent) {
        ids.push(parent.id);
    }
    return ids;
}

export interface EnclosingLoop {
    foreach: IElement;
    variableName: string;
}

/**
 * For a target path being mapped, returns the set of existing mapping paths that would conflict
 * under the "parent and descendant mappings are mutually exclusive" rule.
 *
 * - Descendant mappings under the new path conflict (the new mapping subsumes them).
 * - Ancestor mappings of the new path conflict (the existing mapping subsumes the new one).
 */
export function findConflictingMappings(
    existingMappings: { [path: string]: unknown },
    newPath: string,
    targetSchema?: JsonSchemaProperty[],
): string[] {
    const conflicts: string[] = [];
    const newSegs = resolvePathSegments(newPath, targetSchema);
    for (const path of Object.keys(existingMappings)) {
        if (path !== newPath && segmentsAncestorOrDescendant(resolvePathSegments(path, targetSchema), newSegs)) {
            conflicts.push(path);
        }
    }
    return conflicts;
}

/**
 * Returns target paths whose mapping affordance must be hidden because saving a mapping there
 * would conflict with an existing one (whole-array vs per-element mutex). The conflicting mapping
 * itself is not blocked - the user can still edit or delete it.
 */
export function findBlockedTargetPaths(
    targetSchema: JsonSchemaProperty[] | undefined,
    mappings: { [path: string]: unknown } | undefined,
): Set<string> {
    const blocked = new Set<string>();
    if (!targetSchema || !mappings) {
        return blocked;
    }

    const visit = (schema: JsonSchemaProperty[], parent: string) => {
        for (const prop of schema) {
            const path = buildSchemaPath(parent, prop);
            if (mappings[path] === undefined && findConflictingMappings(mappings, path, targetSchema).length > 0) {
                blocked.add(path);
            }
            if (prop.children) {
                visit(prop.children, path);
            }
        }
    };
    visit(targetSchema, "");
    return blocked;
}

export function buildSchemaPath(parentPath: string, prop: JsonSchemaProperty): string {
    return buildPath(parentPath, prop.pathSegment ?? prop.key);
}

function buildPath(parentPath: string, key: string): string {
    if (!parentPath) {
        return key;
    }

    if (!key) {
        return parentPath;
    }

    return parentPath ? `${parentPath}.${key}` : key;
}

/**
 * Resolves a dotted path to its schema segment chain, matching segments against real keys (which may
 * contain dots) so a literal "user.name" is not read as the nested path user -> name. Mirrors backend
 * VdmEmitBuilder.MatchPath; falls back to a plain split with no schema or a stale/ambiguous path.
 */
function resolvePathSegments(path: string, schema?: JsonSchemaProperty[]): string[] {
    const schemaPath = normalizePathForSchema(path);
    if (schema) {
        const matches: JsonSchemaProperty[][] = [];
        matchPathNodes(schema, schemaPath, [], matches);
        if (matches.length === 1) {
            // A transparent wrapper contributes no segment, matching how buildPath collapses it.
            return matches[0].map((node) => node.pathSegment ?? node.key).filter((seg) => seg !== "");
        }
    }
    return schemaPath === "" ? [] : schemaPath.split(".");
}

function matchPathNodes(
    nodes: JsonSchemaProperty[],
    remaining: string,
    prefix: JsonSchemaProperty[],
    matches: JsonSchemaProperty[][],
): void {
    for (const node of nodes) {
        const seg = node.pathSegment ?? node.key;
        if (seg === "") {
            // Transparent root-array wrapper: the empty path is its whole-array mapping; its children
            // sit at the same level (buildPath collapses the empty segment). It joins the chain
            // either way, since as an ancestor it is one of the array scopes enclosing the path.
            if (remaining === "") {
                matches.push([...prefix, node]);
            }
            if (node.children) {
                matchPathNodes(node.children, remaining, [...prefix, node], matches);
            }
            continue;
        }
        if (remaining === seg) {
            matches.push([...prefix, node]);
        } else if (node.children
            && remaining.length > seg.length
            && remaining.startsWith(seg)
            && remaining[seg.length] === ".") {
            matchPathNodes(node.children, remaining.slice(seg.length + 1), [...prefix, node], matches);
        }
    }
}

// Mutex ancestry: one segment chain is a prefix of the other (equal included; [] is a prefix of all).
function segmentsAncestorOrDescendant(a: string[], b: string[]): boolean {
    return isSegmentPrefix(a, b) || isSegmentPrefix(b, a);
}

function isSegmentPrefix(prefix: string[], full: string[]): boolean {
    return prefix.length <= full.length && prefix.every((seg, i) => seg === full[i]);
}